/**
 * MktPanel.js - Panel de Marketing y Ventas
 * Vista para gestionar liberación de tags y métricas de conversión
 */
import React, { useState, useEffect, useCallback } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { initFirebase } from "../../firebaseConfig";
import { signInWithGoogle, signOutUser } from "../../services/api/modules/auth";
import { getAuthHeaders } from "../../services/api";

import "bootstrap/dist/css/bootstrap.min.css";
import "./MktPanel.css";

// Tabs
import DashboardTab from "./components/DashboardTab";
import StockTab from "./components/StockTab";
import VentasTab from "./components/VentasTab";
import ConversionTab from "./components/ConversionTab";

function MktPanel() {
  // Estado de autenticación
  const [adminUser, setAdminUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Estado de tabs
  const [activeTab, setActiveTab] = useState("dashboard");

  // Estado de métricas
  const [metricas, setMetricas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Auth setup
  useEffect(() => {
    let unsubscribe;

    async function setupAuth() {
      try {
        const { auth } = await initFirebase();

        unsubscribe = onAuthStateChanged(auth, (user) => {
          setAdminUser(user);
          setAuthLoading(false);

          if (user) {
            loadMetricas();
          }
        });
      } catch (error) {
        console.error('Error inicializando auth:', error);
        setAuthLoading(false);
      }
    }

    setupAuth();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Cargar métricas
  const loadMetricas = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/objetos/mkt/metricas', { headers });

      if (!response.ok) {
        throw new Error('Error al cargar métricas');
      }

      const data = await response.json();
      setMetricas(data.data);
    } catch (err) {
      console.error('Error cargando métricas:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Loading de autenticación
  if (authLoading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Verificando sesión...</span>
        </div>
        <p className="mt-3">Verificando sesión...</p>
      </div>
    );
  }

  // Sin autenticación
  if (!adminUser) {
    return (
      <div className="container mt-5 text-center">
        <h2 className="mb-3">MKT Panel - Acceso Restringido</h2>
        <p className="text-muted mb-4">Necesitas iniciar sesión como administrador</p>
        <button
          onClick={() => signInWithGoogle().catch(console.error)}
          className="btn btn-warning"
        >
          Iniciar Sesión con Google
        </button>
      </div>
    );
  }

  return (
    <div className="mkt-panel min-vh-100 bg-light">
      {/* Indicador de carga */}
      {loading && (
        <div className="position-fixed top-0 start-0 w-100" style={{ zIndex: 9999 }}>
          <div className="progress" style={{ height: '3px' }}>
            <div className="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              style={{ width: '100%' }}></div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="top-bar bg-white border-bottom sticky-top">
        <div className="container-fluid p-3">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <h4 className="mb-0">
                <i className="bi bi-graph-up-arrow me-2 text-success"></i>
                MKT Panel
              </h4>
              <span className="badge bg-secondary">Ventas y Conversión</span>
            </div>

            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={loadMetricas}
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise"></i> Actualizar
              </button>
              <a href="/admin-ops" className="btn btn-outline-secondary btn-sm" title="Admin Operativo">
                <i className="bi bi-gear"></i>
              </a>
              <a href="/dashboard" className="btn btn-outline-secondary btn-sm" title="Dashboard">
                <i className="bi bi-house"></i>
              </a>
              <button onClick={signOutUser} className="btn btn-outline-danger btn-sm">
                <i className="bi bi-box-arrow-right"></i>
              </button>
            </div>
          </div>

          {/* Navegación de tabs */}
          <ul className="nav nav-tabs mt-3 mb-0">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => setActiveTab('dashboard')}
              >
                <i className="bi bi-speedometer2 me-2"></i>
                Dashboard
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'stock' ? 'active' : ''}`}
                onClick={() => setActiveTab('stock')}
              >
                <i className="bi bi-box-seam me-2"></i>
                Stock
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'ventas' ? 'active' : ''}`}
                onClick={() => setActiveTab('ventas')}
              >
                <i className="bi bi-currency-dollar me-2"></i>
                Ventas
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'conversion' ? 'active' : ''}`}
                onClick={() => setActiveTab('conversion')}
              >
                <i className="bi bi-funnel me-2"></i>
                Conversión
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="main-content-area">
        <div className="container-fluid p-3">
          {error && (
            <div className="alert alert-danger" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          {activeTab === 'dashboard' && (
            <DashboardTab
              metricas={metricas}
              loading={loading}
              onRefresh={loadMetricas}
            />
          )}

          {activeTab === 'stock' && (
            <StockTab
              onRefresh={loadMetricas}
            />
          )}

          {activeTab === 'ventas' && (
            <VentasTab />
          )}

          {activeTab === 'conversion' && (
            <ConversionTab
              metricas={metricas}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default MktPanel;
